import { useEffect, useRef, useCallback } from 'react';
import { useLazyGetApplianceByIdQuery } from '../../../api';

/**
 * Migration Appliance Polling Hook
 */
export const useMigrationAppliancePolling = (cloudAccounts, onApplianceUpdate) => {
    const [fetchApplianceById] = useLazyGetApplianceByIdQuery();
    const pollingIntervalsRef = useRef(new Map());
    // Track last known states to avoid unnecessary re-renders
    const lastStatesRef = useRef(new Map());

    /**
     * Check if an appliance should be polled based on its current state
     */
    const shouldPollAppliance = useCallback((appliance) => {
        if (!appliance) {
            return false;
        }

        const upgradeState = appliance.upgrade_state;
        const processState = appliance.process_state;

        return (
            (upgradeState && ['pending', 'running'].includes(upgradeState)) ||
            (processState && ['pending', 'running'].includes(processState))
        );
    }, []);

    /**
     * Fetch updated appliance data from the API
     */
    const fetchApplianceData = useCallback(
        async (applianceId) => {
            try {
                const result = await fetchApplianceById({ applianceId });

                if (result.error) {
                    console.error(`Error fetching appliance ${applianceId}:`, result.error);
                    return null;
                }

                return result.data;
            } catch (error) {
                console.error(`Exception fetching appliance ${applianceId}:`, error);
                return null;
            }
        },
        [fetchApplianceById],
    );

    /**
     * Stop polling a specific appliance
     */
    const stopPolling = useCallback((applianceId) => {
        const intervalId = pollingIntervalsRef.current.get(applianceId);

        if (intervalId) {
            clearInterval(intervalId);
            pollingIntervalsRef.current.delete(applianceId);
            lastStatesRef.current.delete(applianceId);
        }
    }, []);

    /**
     * Start polling a specific appliance
     * Polls every 10 seconds until state is no longer "pending" or "running"
     */
    const startPolling = useCallback(
        (applianceId) => {
            // Don't start if already polling this appliance
            if (pollingIntervalsRef.current.has(applianceId)) {
                return;
            }

            // Create polling interval (10 seconds, matching Backbone)
            const intervalId = setInterval(async () => {
                const updatedAppliance = await fetchApplianceData(applianceId);

                if (!updatedAppliance) {
                    stopPolling(applianceId);
                    return;
                }

                // Check if state has changed
                const currentUpgradeState = updatedAppliance.upgrade_state;
                const currentProcessState = updatedAppliance.process_state;

                // Update last known state
                lastStatesRef.current.set(applianceId, {
                    upgrade_state: currentUpgradeState,
                    process_state: currentProcessState,
                });

                // Notify parent component of the update
                onApplianceUpdate(applianceId, updatedAppliance);

                // Check if we should continue polling
                if (!shouldPollAppliance(updatedAppliance)) {
                    stopPolling(applianceId);
                }
            }, 10000);

            // Store the interval ID
            pollingIntervalsRef.current.set(applianceId, intervalId);
        },
        [fetchApplianceData, onApplianceUpdate, shouldPollAppliance, stopPolling],
    );

    /**
     * Stop all active polling
     */
    const stopAllPolling = useCallback(() => {
        pollingIntervalsRef.current.forEach((intervalId) => {
            clearInterval(intervalId);
        });

        pollingIntervalsRef.current.clear();
        lastStatesRef.current.clear();
    }, []);

    /**
     * Effect: Auto-start/stop polling based on cloud accounts data
     * Replicates Backbone's handleGridResetProgressTracking method
     */
    useEffect(() => {
        if (!cloudAccounts || !Array.isArray(cloudAccounts)) {
            return;
        }

        // Find all appliances that need polling
        const applianceIdsToPoll = cloudAccounts
            .filter((cloudAccount) => cloudAccount.appliance && shouldPollAppliance(cloudAccount.appliance))
            .map((cloudAccount) => cloudAccount.appliance.id);

        // Start polling for new appliances
        applianceIdsToPoll.forEach((applianceId) => {
            if (!pollingIntervalsRef.current.has(applianceId)) {
                startPolling(applianceId);
            }
        });

        // Stop polling for appliances that no longer need it
        const currentlyPollingIds = Array.from(pollingIntervalsRef.current.keys());
        currentlyPollingIds.forEach((applianceId) => {
            if (!applianceIdsToPoll.includes(applianceId)) {
                stopPolling(applianceId);
            }
        });
    }, [cloudAccounts, shouldPollAppliance, startPolling, stopPolling]);

    /**
     * Effect: Cleanup on unmount
     */
    useEffect(() => {
        return () => {
            stopAllPolling();
        };
    }, [stopAllPolling]);

    /**
     * Effect: Register global cleanup function for page navigation
     * This allows Backbone code to clean up React polling when navigating away
     */
    useEffect(() => {
        // Store the original function if it exists
        const originalCleanup = window.clearMigrationAppliancePolling;

        // Register our cleanup function
        window.clearMigrationAppliancePolling = () => {
            stopAllPolling();
            // Call original if it existed (for Backbone compatibility)
            if (originalCleanup && typeof originalCleanup === 'function') {
                originalCleanup();
            }
        };

        return () => {
            // Cleanup
            if (window.clearMigrationAppliancePolling === stopAllPolling) {
                delete window.clearMigrationAppliancePolling;
            }
        };
    }, [stopAllPolling]);

    return {
        pollingCount: pollingIntervalsRef.current.size,
        startPolling,
        stopPolling,
        stopAllPolling,
    };
};
